/*
 * Copyright (c) 2018-2019, F5 Networks, Inc. All rights reserved.
 *
 * No part of this software may be reproduced or transmitted in any
 * form or by any means, electronic or mechanical, for any purpose,
 * without express written permission of F5 Networks, Inc.
 */
export interface IRequestOptions {
    /**
     * 'Accept' request header value
     */
    accept?: string;
    /**
     * Request body
     */
    body: string | null;
    /**
     * Request body type
     */
    contentType?: string;
    /**
     * Make synchronous request.
     */
    sync: boolean;
    /**
     * HTTP request method
     */
    method: string;
    /**
     * Optional timeout to abort the request and reject the promise (ms).
     */
    timeout: number;
}

const defaultOptions: IRequestOptions = {
    body: null,
    method: 'GET',
    sync: false,
    timeout: 0,
};

declare const ActiveXObject: any;

/**
 * Cross-browser XMLHttpRequest constructor
 */
export function getHttpRequestObject(): XMLHttpRequest {
    let res = null;
    try {
        res = new XMLHttpRequest();
    } catch (e) {
        try {
            res = new ActiveXObject('Msxml2.XMLHTTP');
        } catch (e) {
            try {
                res = new ActiveXObject('Microsoft.XMLHTTP');
            } catch (e) {
                res = null;
            }
        }
    }
    return res;
}

export class HTTPError extends Error {
    __proto__: Error;

    public code: number;
    constructor(code: number, message: string) {
        super(message);

        const trueProto = new.target.prototype;
        this.__proto__ = trueProto;

        this.code = code;
        this.name = 'HTTPError';
    }
}

/**
 * Send a HTTP request and return Promise<XMLHttpRequest> which resolves as request became complete.
 * @param url     Request URL
 * @param options An optional object with additional options as described by RequestOptions interface.
 */
function request(url: string, options?: Partial<IRequestOptions>): Promise<XMLHttpRequest> {
    const opts: IRequestOptions = {
        ...defaultOptions,
        ...options,
    };
    const xhr = getHttpRequestObject();
    xhr.open(opts.method, url, !opts.sync);

    if (opts.accept) {
        xhr.setRequestHeader('Accept', opts.accept);
    }

    if (opts.contentType) {
        xhr.setRequestHeader('Content-Type', opts.contentType);
    }

    if (opts.sync) {
        xhr.send(opts.body);
        return Promise.resolve(xhr);
    }

    return new Promise((resolve, reject) => {
        let completed = false;
        const errorHandler = (err: Event) => {
            if (!completed) {
                completed = true;
                reject(err);
            }
        };
        xhr.onabort = errorHandler;
        xhr.onerror = errorHandler;
        xhr.onload = () => {
            completed = true;
            if (xhr.status / 100 === 2 || xhr.status === 0) {
                resolve(xhr);
            } else {
                reject(new HTTPError(xhr.status, xhr.statusText || 'HTTP error'));
            }
        };
        if (opts.timeout > 0) {
            if (typeof xhr.timeout !== 'undefined') {
                xhr.ontimeout = errorHandler;
                xhr.timeout = opts.timeout;
            } else {
                /* Compatibility with older XHR implementations */
                window.setTimeout(() => {
                    if (!completed) {
                        xhr.abort();
                        completed = true;
                        reject(new Error('Request timed out'));
                    }
                }, opts.timeout);
            }
        }

        xhr.send(opts.body);
    });
}

export { request as default, request };
